import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  NotebookText, Plus, Search, Filter, LayoutGrid, LayoutList, StickyNote,
  FileText, Loader2, AlertCircle, X,
} from 'lucide-react'
import {
  Button, Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Popover, PopoverTrigger, PopoverContent,
} from '@goodday-clone/ui'
import { cn } from '@/shared/utils/utils'
import { useAuth } from '@/context/AuthContext'
import { usePermissions } from '@/shared/hooks/usePermissions'
import NotepadSidebar from './notepad/NotepadSidebar'
import { NoteCard, NoteRow } from './notepad/NoteCard'
import NoteBoard from './notepad/NoteBoard'
import type { BoardActions } from './notepad/NoteBoard'
import { toggleTaskInSource, addTaskToSource, addSubtaskToSource } from './notepad/markdown'
import {
  useNotesList, useNoteFolders, useCreateNote, useUpdateNote, useDeleteNote,
  useRestoreNote, useDeleteNotePermanent, useDuplicateNote,
} from './notepad/notesApi'
import type { Note, NoteColor, NotesView } from './notepad/notesApi'

const DATE_TABS: { key: 'all' | 'today' | 'week' | 'month'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
]

const VIEW_TITLES: Record<NotesView, string> = {
  all: 'All Notes',
  mine: 'My Notes',
  shared: 'Shared with Me',
  starred: 'Starred',
  trash: 'Trash',
}

export default function NotepadPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasPermission } = usePermissions()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '')

  const view = (searchParams.get('view') || 'all') as NotesView
  const folderId = searchParams.get('folder') || ''
  const department = searchParams.get('dept') || ''
  const dateRange = (searchParams.get('range') || 'all') as 'all' | 'today' | 'week' | 'month'
  const priority = searchParams.get('priority') || ''
  const layout = (searchParams.get('layout') || 'grid') as 'grid' | 'list' | 'board'
  const page = parseInt(searchParams.get('page') || '1') || 1

  const setParam = useCallback((patch: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      Object.entries(patch).forEach(([k, v]) => {
        if (v) next.set(k, v)
        else next.delete(k)
      })
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setView = (v: NotesView) => {
    setParam({ view: v === 'all' ? '' : v, folder: '' })
  }
  const selectFolder = (id: string) => {
    setParam({ folder: id, view: id ? '' : view === 'all' ? '' : view })
  }
  const selectDepartment = (dept: string) => setParam({ dept: dept })

  const listQuery = useNotesList({
    view,
    folderId: view === 'all' ? folderId : undefined,
    department: department || undefined,
    dateRange: dateRange === 'all' ? undefined : dateRange,
    priority: priority || undefined,
    search: searchParams.get('q') || undefined,
    page,
    limit: 24,
  })
  const foldersQuery = useNoteFolders()
  const folders = foldersQuery.data || []

  const createNote = useCreateNote()
  const updateNote = useUpdateNote()
  const trashNote = useDeleteNote()
  const restoreNote = useRestoreNote()
  const purgeNote = useDeleteNotePermanent()
  const duplicateNote = useDuplicateNote()

  const notes = listQuery.data?.data || []
  const counts = listQuery.data?.counts
  const pagination = listQuery.data?.pagination
  const isPrivileged = user?.role === 'SUPERADMIN' || user?.role === 'ADMIN'

  const canEditNote = useCallback((note: Note) => {
    if (isPrivileged) return true
    if (String(note.authorId && typeof note.authorId === 'object' ? note.authorId._id : note.authorId) === user?.id) return true
    return (note.collaborators || []).some((c) => {
      const id = typeof c.userId === 'string' ? c.userId : c.userId?._id
      return id === user?.id && c.access === 'EDIT'
    })
  }, [isPrivileged, user?.id])

  const canDeleteNote = useCallback((note: Note) => {
    if (user?.role === 'SUPERADMIN') return true
    const authorId = note.authorId && typeof note.authorId === 'object' ? note.authorId._id : note.authorId
    return Boolean(authorId && String(authorId) === user?.id)
  }, [user?.id, user?.role])

  const handleNewNote = async () => {
    if (!hasPermission('NOTES', 'CREATE')) return
    try {
      const note = await createNote.mutateAsync({ title: 'Untitled note', folderId: folderId || undefined })
      navigate(`/app/notepad/${note._id}`, { state: { from: window.location.pathname + window.location.search, focusTitle: true } })
    } catch { /* toast already shown */ }
  }

  const cardActions = useMemo(() => ({
    onToggleStar: (note: Note) => updateNote.mutate({ id: note._id, data: { isStarred: !note.isStarred } }),
    onTogglePin: (note: Note) => updateNote.mutate({ id: note._id, data: { isPinned: !note.isPinned } }),
    onDuplicate: (note: Note) => duplicateNote.mutate(note._id),
    onTrash: (note: Note) => trashNote.mutate(note._id),
    onRestore: (note: Note) => restoreNote.mutate(note._id),
    onDeletePermanent: (note: Note) => purgeNote.mutate(note._id),
    onMoveToFolder: (note: Note, targetFolderId: string | null) =>
      updateNote.mutate({ id: note._id, data: { folderId: targetFolderId } }),
    onShare: (note: Note) => navigate(`/app/notepad/${note._id}`, { state: { openShare: true } }),
    folders,
    canEdit: canEditNote,
    canDelete: canDeleteNote,
  }), [updateNote, duplicateNote, trashNote, restoreNote, purgeNote, folders, canEditNote, canDeleteNote, navigate])

  const boardActions = useMemo<BoardActions>(() => ({
    onToggleTask: (note, taskIndex) =>
      updateNote.mutate({ id: note._id, data: { content: toggleTaskInSource(note.content || '', taskIndex) } }),
    onAddTask: (note, text) =>
      updateNote.mutate({ id: note._id, data: { content: addTaskToSource(note.content || '', text) } }),
    onAddSubtask: (note, parentTaskIndex, text) =>
      updateNote.mutate({ id: note._id, data: { content: addSubtaskToSource(note.content || '', parentTaskIndex, text) } }),
    onSetColor: (note, color: NoteColor) =>
      updateNote.mutate({ id: note._id, data: { color } }),
  }), [updateNote])

  const hasActiveFilters = !!(priority || (dateRange && dateRange !== 'all') || department)

  return (
    <div className="flex h-full min-h-0 bg-slate-50/60">
      <NotepadSidebar
        view={folderId ? 'all' : view}
        folderId={folderId}
        department={department}
        counts={counts}
        folders={folders}
        onNavigate={setView}
        onSelectFolder={selectFolder}
        onSelectDepartment={selectDepartment}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-5 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2.5 mr-auto">
            <span className="h-9 w-9 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
              <NotebookText className="h-5 w-5 text-white" />
            </span>
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-tight">Notepad</h1>
              <p className="text-[11px] text-slate-500 leading-tight">Your notes, organized</p>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search notes..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setParam({ q: searchInput.trim(), page: '1' }) }}
              className="pl-9 w-56 lg:w-72 h-9 rounded-lg border-slate-200"
            />
            {searchParams.get('q') && (
              <button
                onClick={() => { setSearchInput(''); setParam({ q: '', page: '1' }) }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className={cn('h-9 w-9 rounded-lg border-slate-200', hasActiveFilters && 'border-emerald-300 text-emerald-700 bg-emerald-50/50')}>
                <Filter className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-4">
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-900">Filters</p>
                <div>
                  <Label className="text-xs font-medium text-slate-500 block mb-1.5">Priority</Label>
                  <Select value={priority || 'all'} onValueChange={(v) => setParam({ priority: v === 'all' ? '' : v, page: '1' })}>
                    <SelectTrigger className="w-full h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any priority</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="LOW">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 block mb-1.5">Updated</Label>
                  <Select value={dateRange} onValueChange={(v) => setParam({ range: v === 'all' ? '' : v, page: '1' })}>
                    <SelectTrigger className="w-full h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any time</SelectItem>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">This week</SelectItem>
                      <SelectItem value="month">This month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" className="w-full text-slate-500" onClick={() => setParam({ priority: '', range: '', page: '1' })}>
                    Clear filters
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1 bg-white">
            <button
              onClick={() => setParam({ layout: 'grid' })}
              className={cn('p-1.5 rounded transition-colors', layout === 'grid' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-400 hover:text-slate-600')}
              title="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setParam({ layout: 'list' })}
              className={cn('p-1.5 rounded transition-colors', layout === 'list' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-400 hover:text-slate-600')}
              title="List view"
            >
              <LayoutList className="h-4 w-4" />
            </button>
            <button
              onClick={() => setParam({ layout: 'board' })}
              className={cn('p-1.5 rounded transition-colors', layout === 'board' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-400 hover:text-slate-600')}
              title="Board view"
            >
              <StickyNote className="h-4 w-4" />
            </button>
          </div>

          {hasPermission('NOTES', 'CREATE') && (
            <Button onClick={handleNewNote} disabled={createNote.isPending} className="gap-2 bg-emerald-700 hover:bg-emerald-800 h-9 rounded-lg">
              {createNote.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              New Note
            </Button>
          )}
        </div>

        {/* Date tabs */}
        <div className="bg-white border-b border-slate-200 px-5 flex items-center gap-1">
          {DATE_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setParam({ range: tab.key === 'all' ? '' : tab.key, page: '1' })}
              className={cn(
                'px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                dateRange === tab.key
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
            >
              {tab.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-400 hidden md:block">
            {VIEW_TITLES[folderId ? 'all' : view]}{folderId ? ` · ${folders.find((f) => f._id === folderId)?.name || 'Folder'}` : ''}
          </span>
        </div>

        {/* Listing */}
        <div className="flex-1 overflow-auto p-5">
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : listQuery.isError ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <AlertCircle className="h-12 w-12 text-rose-400 mb-3" />
              <h3 className="text-base font-semibold text-slate-900 mb-1">Couldn't load notes</h3>
              <p className="text-sm text-slate-500 mb-4">Something went wrong. Please try again.</p>
              <Button variant="outline" size="sm" onClick={() => listQuery.refetch()}>Retry</Button>
            </div>
          ) : notes.length === 0 ? (
            view === 'trash' ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <FileText className="h-14 w-14 text-slate-300 mb-3" />
                <h3 className="text-base font-semibold text-slate-900 mb-1">Trash is empty</h3>
                <p className="text-sm text-slate-500">Deleted notes will appear here.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <FileText className="h-14 w-14 text-slate-300 mb-3" />
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  {searchParams.get('q') || hasActiveFilters || folderId ? 'No notes match your filters' : 'No notes yet'}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  {searchParams.get('q') || hasActiveFilters || folderId
                    ? 'Try adjusting the search or filters.'
                    : 'Create your first note to get started'}
                </p>
                {searchParams.get('q') || hasActiveFilters || folderId ? (
                  <Button variant="outline" size="sm" onClick={() => setParam({ q: '', priority: '', range: '', folder: '', dept: '' })}>
                    Clear filters
                  </Button>
                ) : (
                  hasPermission('NOTES', 'CREATE') && (
                    <Button onClick={handleNewNote} className="gap-2 bg-emerald-700 hover:bg-emerald-800">
                      <Plus className="h-4 w-4" /> Create Note
                    </Button>
                  )
                )}
              </div>
            )
          ) : layout === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {notes.map((note) => <NoteCard key={note._id} note={note} actions={cardActions} />)}
            </div>
          ) : layout === 'board' ? (
            <NoteBoard notes={notes} actions={cardActions} board={boardActions} busy={updateNote.isPending} />
          ) : (
            <div className="space-y-2 max-w-5xl">
              {notes.map((note) => <NoteRow key={note._id} note={note} actions={cardActions} />)}
            </div>
          )}

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setParam({ page: String(page - 1) })}>
                Previous
              </Button>
              <span className="text-sm text-slate-500">Page {page} of {pagination.totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setParam({ page: String(page + 1) })}>
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
